import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Logo } from "@/components/ui/Logo";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { ROUTES } from "@/constants/routes";
import {
  clearStoredReferralCode,
  getStoredReferralCode,
  normalizeReferralCode,
  storeReferralCode,
} from "@/lib/referralCode";
import { authService } from "@/services/authService";

import {
  PASSWORD_MIN_LENGTH,
  isValidEmail,
  normalize,
  validateRequiredPassword,
} from "@/lib/validateUser";

type FieldName = "email" | "username" | "referralCode" | "password" | "confirmPassword";
type FieldErrors = Partial<Record<FieldName, string>>;

const ERROR_TEXT = {
  emailRequired: "Введите email",
  emailInvalid: "Введите корректный email",
  usernameRequired: "Введите имя пользователя",
  referralCodeInvalid: "Реферальный код должен содержать от 6 до 32 букв или цифр",
  passwordRequired: "Введите пароль",
  passwordTooShort: `Пароль должен содержать минимум ${PASSWORD_MIN_LENGTH} символов`,
  passwordWeak: "Пароль должен содержать хотя бы одну букву и хотя бы одну цифру",
  confirmRequired: "Повторите пароль",
  confirmMismatch: "Пароли не совпадают",
  formHasErrors: "Проверьте поля формы",
  registerError: "Ошибка при регистрации",
} as const;

const errorId = (field: FieldName) => `${field}-error`;

export const RegisterPage = () => {
  const navigate = useNavigate();

  const emailRef = useRef<HTMLInputElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  const referralCodeRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [referralCode, setReferralCode] = useState(() => getStoredReferralCode() ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<FieldErrors>({});

  // После первой попытки отправки показываем ошибки и подсветку
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const clientErrors = useMemo<FieldErrors>(() => {
    const errs: FieldErrors = {};

    const normalizedEmail = normalize(email);
    const normalizedUsername = normalize(username);
    const normalizedReferralCode = normalizeReferralCode(referralCode);

    if (!normalizedEmail) errs.email = ERROR_TEXT.emailRequired;
    else if (!isValidEmail(normalizedEmail)) errs.email = ERROR_TEXT.emailInvalid;

    if (!normalizedUsername) errs.username = ERROR_TEXT.usernameRequired;
    if (referralCode.trim() && !normalizedReferralCode) {
      errs.referralCode = ERROR_TEXT.referralCodeInvalid;
    }

    const passwordError = validateRequiredPassword(password);
    if (passwordError === "required") errs.password = ERROR_TEXT.passwordRequired;
    else if (passwordError === "too_short") errs.password = ERROR_TEXT.passwordTooShort;
    else if (passwordError === "weak") errs.password = ERROR_TEXT.passwordWeak;

    if (!confirmPassword) errs.confirmPassword = ERROR_TEXT.confirmRequired;
    else if (password !== confirmPassword) errs.confirmPassword = ERROR_TEXT.confirmMismatch;

    return errs;
  }, [email, username, referralCode, password, confirmPassword]);

  const clearServerFieldError = (field: FieldName) => {
    setServerFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const focusFirstError = (errors: FieldErrors) => {
    if (errors.email) emailRef.current?.focus();
    else if (errors.username) usernameRef.current?.focus();
    else if (errors.referralCode) referralCodeRef.current?.focus();
    else if (errors.password) passwordRef.current?.focus();
    else if (errors.confirmPassword) confirmPasswordRef.current?.focus();
  };

  // Итоговая ошибка для отображения на поле
  const getDisplayedError = (field: FieldName): string | undefined => {
    if (!submitAttempted) return undefined;
    return serverFieldErrors[field] || clientErrors[field];
  };

  // Блокируем кнопку только после первой попытки submit и только пока остаются ошибки
  const hasErrorsAfterSubmit =
    submitAttempted &&
    (Object.keys(clientErrors).length > 0 || Object.keys(serverFieldErrors).length > 0);

  const isSubmitDisabled = isLoading || hasErrorsAfterSubmit;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitAttempted(true);
    setFormError(null);

    // Сначала валидируем на клиенте, чтобы не делать лишний запрос
    if (Object.keys(clientErrors).length > 0) {
      focusFirstError(clientErrors);
      return;
    }

    setIsLoading(true);

    try {
      const normalizedEmail = normalize(email);
      const normalizedUsername = normalize(username);
      const normalizedReferralCode = normalizeReferralCode(referralCode);

      await authService.register({
        email: normalizedEmail,
        username: normalizedUsername,
        password,
        referral_code: normalizedReferralCode ?? undefined,
      });

      clearStoredReferralCode();
      toast.success("Регистрация прошла успешно");
      navigate(ROUTES.login);
    } catch (err) {
      const message = err instanceof Error ? err.message : ERROR_TEXT.registerError;

      let fieldErrorsFromServer: Record<string, string> | undefined;
      if (err && typeof err === "object" && "fieldErrors" in err) {
        fieldErrorsFromServer = (err as Error & { fieldErrors?: Record<string, string> }).fieldErrors;
      }

      if (fieldErrorsFromServer && Object.keys(fieldErrorsFromServer).length > 0) {
        const mapped: FieldErrors = {};
        if (fieldErrorsFromServer.email) mapped.email = fieldErrorsFromServer.email;
        if (fieldErrorsFromServer.username) mapped.username = fieldErrorsFromServer.username;
        if (fieldErrorsFromServer.referral_code) {
          mapped.referralCode = fieldErrorsFromServer.referral_code;
        }
        if (fieldErrorsFromServer.password) mapped.password = fieldErrorsFromServer.password;

        setServerFieldErrors(mapped);
        setFormError(ERROR_TEXT.formHasErrors);
        focusFirstError(mapped);
      } else {
        setFormError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-12">
      <div className="flex flex-col gap-6">
        <Logo />
        <h1 className="fs-700 font-bold text-neutral-100">Регистрация</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          <Input
            ref={emailRef}
            type="email"
            placeholder="E-mail"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              clearServerFieldError("email");
            }}
            aria-invalid={!!getDisplayedError("email")}
            aria-describedby={getDisplayedError("email") ? errorId("email") : undefined}
            disabled={isLoading}
          />
          {getDisplayedError("email") && (
            <p id={errorId("email")} className="fs-200 text-danger">
              {getDisplayedError("email")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Input
            ref={usernameRef}
            type="text"
            placeholder="Имя пользователя"
            autoComplete="username"
            required
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              clearServerFieldError("username");
            }}
            aria-invalid={!!getDisplayedError("username")}
            aria-describedby={getDisplayedError("username") ? errorId("username") : undefined}
            disabled={isLoading}
          />
          {getDisplayedError("username") && (
            <p id={errorId("username")} className="fs-200 text-danger">
              {getDisplayedError("username")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Input
            ref={referralCodeRef}
            type="text"
            placeholder="Реферальный код (необязательно)"
            autoComplete="off"
            autoCapitalize="characters"
            value={referralCode}
            onChange={(e) => {
              const value = e.target.value;
              setReferralCode(value);
              storeReferralCode(value);
              clearServerFieldError("referralCode");
            }}
            aria-invalid={!!getDisplayedError("referralCode")}
            aria-describedby={
              getDisplayedError("referralCode") ? errorId("referralCode") : undefined
            }
            disabled={isLoading}
          />
          {getDisplayedError("referralCode") && (
            <p
              id={errorId("referralCode")}
              className="fs-200 text-danger"
            >
              {getDisplayedError("referralCode")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <PasswordInput
            ref={passwordRef}
            placeholder="Введите пароль"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearServerFieldError("password");
              clearServerFieldError("confirmPassword");
            }}
            aria-invalid={!!getDisplayedError("password")}
            aria-describedby={getDisplayedError("password") ? errorId("password") : undefined}
            disabled={isLoading}
          />
          {getDisplayedError("password") && (
            <p id={errorId("password")} className="fs-200 text-danger">
              {getDisplayedError("password")}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <PasswordInput
            ref={confirmPasswordRef}
            placeholder="Повторите пароль"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              clearServerFieldError("confirmPassword");
            }}
            aria-invalid={!!getDisplayedError("confirmPassword")}
            aria-describedby={
              getDisplayedError("confirmPassword") ? errorId("confirmPassword") : undefined
            }
            disabled={isLoading}
          />
          {getDisplayedError("confirmPassword") && (
            <p id={errorId("confirmPassword")} className="fs-200 text-danger">
              {getDisplayedError("confirmPassword")}
            </p>
          )}
        </div>

        <div className="flex flex-col items-center gap-4">
          <Button type="submit" size="md" className="w-full" disabled={isSubmitDisabled}>
            {isLoading ? "Загрузка..." : "Зарегистрироваться"}
          </Button>

          <p className="flex items-center gap-1">
            <span className="fs-200 font-medium text-neutral-400">Уже есть аккаунт?</span>
            <Link
              to={ROUTES.login}
              className="text-primary-500 fs-300 font-semibold hover:underline"
            >
              Вход
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};
